import { useEffect, useRef, useState, FormEvent, ChangeEvent } from 'react';
import { Product } from '@/mocks/products';
import { uploadProductImage } from '@/lib/api/products';
import useScrollLock from '@/hooks/useScrollLock';

interface Props {
  product: Product | null;
  onSave: (product: Product) => Promise<void> | void;
  onClose: () => void;
  dark: boolean;
}

function generateId(name: string): string {
  return 'sr-' + name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Date.now().toString(36);
}

const dischargeProfiles: Record<Product['category'], Product['performance']['dischargePoints']> = {
  lithium: [
    { percent: 0, voltage: 13.4 },
    { percent: 10, voltage: 13.2 },
    { percent: 20, voltage: 13.1 },
    { percent: 30, voltage: 13.0 },
    { percent: 40, voltage: 12.9 },
    { percent: 50, voltage: 12.8 },
    { percent: 60, voltage: 12.7 },
    { percent: 70, voltage: 12.6 },
    { percent: 80, voltage: 12.4 },
    { percent: 90, voltage: 12.0 },
    { percent: 100, voltage: 10.0 },
  ],
  agm: [
    { percent: 0, voltage: 12.8 },
    { percent: 10, voltage: 12.6 },
    { percent: 20, voltage: 12.4 },
    { percent: 30, voltage: 12.2 },
    { percent: 40, voltage: 12.0 },
    { percent: 50, voltage: 11.8 },
    { percent: 60, voltage: 11.6 },
    { percent: 70, voltage: 11.4 },
    { percent: 80, voltage: 11.0 },
    { percent: 90, voltage: 10.5 },
    { percent: 100, voltage: 9.5 },
  ],
  gel: [
    { percent: 0, voltage: 12.9 },
    { percent: 10, voltage: 12.7 },
    { percent: 20, voltage: 12.5 },
    { percent: 30, voltage: 12.3 },
    { percent: 40, voltage: 12.1 },
    { percent: 50, voltage: 11.9 },
    { percent: 60, voltage: 11.7 },
    { percent: 70, voltage: 11.5 },
    { percent: 80, voltage: 11.2 },
    { percent: 90, voltage: 10.8 },
    { percent: 100, voltage: 10.0 },
  ],
};

const performancePresets: Record<
  Product['category'],
  Omit<Product['performance'], 'weightKg' | 'dischargePoints'>
> = {
  lithium: { cycleLife: 2000, efficiency: 98, selfDischargeMonthly: 3, chargeTime: 2.5, energyDensity: 94 },
  agm: { cycleLife: 500, efficiency: 80, selfDischargeMonthly: 5, chargeTime: 8, energyDensity: 28 },
  gel: { cycleLife: 700, efficiency: 85, selfDischargeMonthly: 2, chargeTime: 10, energyDensity: 32 },
};

function buildPerformance(
  category: Product['category'],
  weightLbs: number,
  existing?: Product['performance'],
): Product['performance'] {
  if (existing) {
    return {
      ...existing,
      weightKg: Number((weightLbs * 0.453592).toFixed(1)),
    };
  }

  return {
    ...performancePresets[category],
    weightKg: Number((weightLbs * 0.453592).toFixed(1)),
    dischargePoints: dischargeProfiles[category],
  };
}

export default function ProductFormModal({ product, onSave, onClose, dark }: Props) {
  const isEdit = !!product;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  useScrollLock();

  const [form, setForm] = useState<Omit<Product, 'specs' | 'features' | 'performance'> & { specsRaw: string; featuresRaw: string }>({
    id: product?.id || '',
    name: product?.name || '',
    category: product?.category || 'lithium',
    tagline: product?.tagline || '',
    description: product?.description || '',
    voltage: product?.voltage || 12,
    capacity: product?.capacity || 100,
    weight: product?.weight || 0,
    cca: product?.cca || 0,
    dimensions: product?.dimensions || '',
    price: product?.price || 0,
    image: product?.image || '',
    badge: product?.badge || '',
    specsRaw: product ? Object.entries(product.specs).map(([k, v]) => `${k}: ${v}`).join('\n') : '',
    featuresRaw: product?.features.join('\n') || '',
  });
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [previewSrc, setPreviewSrc] = useState(product?.image || '');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[#F97316]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#F97316]';
  const labelCls = dark ? 'text-white/60' : 'text-gray-500';
  const text = dark ? 'text-white' : 'text-gray-900';
  const panelCls = dark ? 'bg-[#0D1B2A] border-white/10' : 'bg-gray-50 border-gray-200';

  const set = (key: string, value: string | number) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!selectedImageFile) {
      setPreviewSrc(form.image);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedImageFile);
    setPreviewSrc(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [form.image, selectedImageFile]);

  const handleImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSubmitError('');

    if (!file) {
      setSelectedImageFile(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setSubmitError('Please choose a valid image file.');
      event.target.value = '';
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      setSubmitError('Image must be smaller than 8 MB.');
      event.target.value = '';
      return;
    }

    setSelectedImageFile(file);
  };

  const clearSelectedImage = () => {
    setSelectedImageFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);
    const specs: Record<string, string> = {};
    form.specsRaw.split('\n').forEach((line) => {
      const idx = line.indexOf(':');
      if (idx > -1) {
        specs[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
      }
    });
    const features = form.featuresRaw.split('\n').map((l) => l.trim()).filter(Boolean);
    const existingPerformance =
      product && product.category === form.category ? product.performance : undefined;
    try {
      const image = selectedImageFile
        ? await uploadProductImage(selectedImageFile)
        : form.image;

      const saved: Product = {
        id: isEdit ? form.id : generateId(form.name),
        name: form.name,
        category: form.category,
        tagline: form.tagline,
        description: form.description,
        voltage: Number(form.voltage),
        capacity: Number(form.capacity),
        weight: Number(form.weight),
        cca: Number(form.cca),
        dimensions: form.dimensions,
        price: Number(form.price),
        image,
        badge: form.badge || undefined,
        specs,
        features,
        performance: buildPerformance(form.category, Number(form.weight), existingPerformance),
      };

      await Promise.resolve(onSave(saved));
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unable to save product.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg ${dark ? 'bg-[#1C2B3A]' : 'bg-white'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${dark ? 'border-white/10' : 'border-gray-200'}`}>
          <h2 className={`font-bold text-base ${text}`}>{isEdit ? 'Edit Product' : 'Add New Product'}</h2>
          <button onClick={onClose} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer ${dark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`}>
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name + Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Product Name *</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Stingray LiFePO4 100Ah"
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`}
              />
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Category *</label>
              <select
                required
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors cursor-pointer ${inputCls}`}
              >
                <option value="lithium">Lithium LiFePO4</option>
                <option value="agm">AGM Series</option>
                <option value="gel">Gel Series</option>
              </select>
            </div>
          </div>

          {/* Tagline */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Tagline</label>
            <input
              type="text"
              value={form.tagline}
              onChange={(e) => set('tagline', e.target.value)}
              placeholder="Short product tagline"
              className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`}
            />
          </div>

          {/* Description */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Product description..."
              className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors resize-none ${inputCls}`}
            />
          </div>

          {/* Specs row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Voltage (V)</label>
              <input type="number" required value={form.voltage} onChange={(e) => set('voltage', e.target.value)}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Capacity (Ah)</label>
              <input type="number" required value={form.capacity} onChange={(e) => set('capacity', e.target.value)}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Weight (lbs)</label>
              <input type="number" step="0.1" value={form.weight} onChange={(e) => set('weight', e.target.value)}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Price ($)</label>
              <input type="number" required value={form.price} onChange={(e) => set('price', e.target.value)}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
          </div>

          {/* Dimensions + CCA + Badge */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Dimensions</label>
              <input type="text" value={form.dimensions} onChange={(e) => set('dimensions', e.target.value)}
                placeholder='12" x 6" x 8"'
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>CCA (0 if N/A)</label>
              <input type="number" value={form.cca} onChange={(e) => set('cca', e.target.value)}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Badge (optional)</label>
              <input type="text" value={form.badge} onChange={(e) => set('badge', e.target.value)}
                placeholder="Best Seller"
                className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`} />
            </div>
          </div>

          {/* Image */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px] gap-4">
            <div className="space-y-4">
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Image URL</label>
                <input
                  type="text"
                  value={form.image}
                  onChange={(e) => set('image', e.target.value)}
                  placeholder="https://..."
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`}
                />
              </div>

              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Upload Image</label>
                <div className={`rounded-lg border p-3 ${panelCls}`}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleImageSelect}
                    className={`block w-full text-sm ${dark ? 'text-white/70 file:bg-white/10 file:text-white' : 'text-gray-600 file:bg-white file:text-gray-700'} file:mr-3 file:rounded-md file:border-0 file:px-3 file:py-2 file:text-sm file:font-semibold`}
                  />
                  <p className={`mt-2 text-xs ${labelCls}`}>
                    JPG, PNG, WEBP, or GIF. Selected file overrides the URL when you save.
                  </p>
                  {selectedImageFile && (
                    <p className={`mt-2 text-xs ${dark ? 'text-white/80' : 'text-gray-700'}`}>
                      Selected file: <span className="font-semibold">{selectedImageFile.name}</span>
                    </p>
                  )}
                  {selectedImageFile && (
                    <button
                      type="button"
                      onClick={clearSelectedImage}
                      className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#F97316] hover:underline"
                    >
                      <i className="ri-close-line"></i>
                      Remove selected file
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Preview</label>
              <div className={`rounded-lg border p-3 ${panelCls}`}>
                <div className={`aspect-[4/3] overflow-hidden rounded-lg border ${dark ? 'border-white/10 bg-[#07101C]' : 'border-gray-200 bg-white'}`}>
                  {previewSrc ? (
                    <img src={previewSrc} alt={form.name || 'Product preview'} className="w-full h-full object-cover object-center" />
                  ) : (
                    <div className={`w-full h-full flex flex-col items-center justify-center text-center px-4 ${labelCls}`}>
                      <i className="ri-image-line text-2xl mb-2"></i>
                      <p className="text-xs">Add an image URL or upload a product photo.</p>
                    </div>
                  )}
                </div>
                <p className={`mt-2 text-xs ${labelCls}`}>
                  {selectedImageFile
                    ? 'Previewing the local file that will be uploaded on save.'
                    : previewSrc
                    ? 'Previewing the current product image.'
                    : 'No image selected yet.'}
                </p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Features (one per line)</label>
            <textarea
              rows={4}
              value={form.featuresRaw}
              onChange={(e) => set('featuresRaw', e.target.value)}
              placeholder={'10-Year Design Life\n2000+ Charge Cycles\nBuilt-in BMS Protection'}
              className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors resize-none font-mono ${inputCls}`}
            />
          </div>

          {/* Specs */}
          <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${labelCls}`}>Technical Specs (Key: Value, one per line)</label>
            <textarea
              rows={5}
              value={form.specsRaw}
              onChange={(e) => set('specsRaw', e.target.value)}
              placeholder={'Nominal Voltage: 12.8V\nCapacity: 100Ah\nWarranty: 5 Years'}
              className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors resize-none font-mono ${inputCls}`}
            />
          </div>

          {submitError && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
              {submitError}
            </div>
          )}

          {/* Actions */}
          <div className={`flex gap-3 pt-2 border-t ${dark ? 'border-white/10' : 'border-gray-100'}`}>
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap border transition-colors ${dark ? 'border-white/20 text-white/70 hover:bg-white/10' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-[#F97316] text-white hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
